import { Button } from '@components/ui/button';
import { useAsset } from '@hooks/useAsset';
import { useDownloadDocument } from '@hooks/useDownloadDocument';
import { assetService } from '@services/api';
import { useStore } from '@store/index';
import { Download, FileText, Loader2, Upload, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import type { FormField } from '@/types/formSchema';

// Defaults when the field's schema declares no file constraints. Mirror the backend's
// fallback rules (service.defaultFileConstraints: single PDF, 10 MB).
const DEFAULT_MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const DEFAULT_ACCEPTED_FORMATS = ['.pdf'];
const USER_ATTACHMENT = 'user_attachment';

const formatSize = (bytes?: number): string => {
  if (!bytes || bytes <= 0) return '';
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

/**
 * Extract the server's sanitized error detail from an upload failure. The
 * backend responds {error, message, code}; `error` names the exact rule that
 * failed (e.g. 'file content (image/jpeg) does not match its .pdf extension'),
 * which the user needs in order to fix the file.
 */
const uploadErrorMessage = (e: unknown): string => {
  const data = (e as any)?.response?.data;
  return data?.error || data?.message || 'Failed to upload file. Please try again.';
};

/** A single entry of a file field's value: a pending File or an uploaded asset id. */
type FileEntry = File | string;

/** Normalizes the stored value (scalar for single-file fields, array for multi) to a list. */
const toEntries = (value: unknown): FileEntry[] => {
  if (Array.isArray(value)) {
    return value.filter((v): v is FileEntry => v instanceof File || (typeof v === 'string' && !!v));
  }
  if (value instanceof File) return [value];
  if (typeof value === 'string' && value) return [value];
  return [];
};

interface FileFieldProps {
  /**
   * Placed on the wrapper, not on a control: which button is visible depends on whether any
   * file is selected, and the `<input type="file">` is deliberately hidden, so the wrapper is
   * the only stable thing a caller can focus this field by.
   */
  id?: string;
  field: FormField;
  /**
   * Legacy fields (no fileMetadata): File (pending upload, Manual Create) |
   * string (asset ID) | undefined. Declared fields (field.fileMetadata):
   * always an array of the same.
   */
  value: unknown;
  onChange: (value: File | string | (File | string)[] | undefined) => void;
  disabled?: boolean;
  error?: string;
  /**
   * When provided, the field belongs to an existing case: files are uploaded immediately on
   * selection and the stored values become asset IDs. When absent (Manual Case Creation), the
   * Files are kept in form state and uploaded together with the case on submit.
   */
  caseRecordId?: string;
}

/** One row of the selected-files list: name/size, download for uploaded assets, remove. */
const FileEntryRow: React.FC<{
  entry: FileEntry;
  fieldLabel: string;
  caseRecordId?: string;
  localMeta?: { name: string; size: number };
  disabled?: boolean;
  error?: boolean;
  onRemove: () => void;
}> = ({ entry, fieldLabel, caseRecordId, localMeta, disabled, error, onRemove }) => {
  const assetId = typeof entry === 'string' ? entry : undefined;
  const { data: asset, isLoading: isAssetLoading } = useAsset(assetId, caseRecordId);
  const { downloadDoc, isDownloading } = useDownloadDocument();

  const fileName =
    entry instanceof File
      ? entry.name
      : localMeta?.name || asset?.asset_name || asset?.file_path || '';
  const fileSize = entry instanceof File ? entry.size : (localMeta?.size ?? asset?.size);
  // Fetching metadata for an uploaded asset we don't have a local name for (e.g. on reload).
  const isMetaLoading = !!assetId && isAssetLoading && !localMeta?.name;

  const handleDownload = (): void => {
    if (!assetId || !caseRecordId) return;
    downloadDoc({
      assetId,
      caseRecordId,
      filename: asset?.asset_name || asset?.file_path || `${fieldLabel || 'attachment'}.pdf`,
    });
  };

  return (
    <div
      className={`flex items-center gap-2 rounded-md border p-2 ${error ? 'border-destructive' : ''}`}
    >
      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="flex min-w-0 flex-1 flex-col text-left">
        {isMetaLoading ? (
          <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading…
          </span>
        ) : (
          <>
            <span className="truncate text-sm">{fileName || 'Attachment'}</span>
            {formatSize(fileSize) && (
              <span className="text-xs text-muted-foreground">{formatSize(fileSize)}</span>
            )}
          </>
        )}
      </div>
      {assetId && caseRecordId && !isMetaLoading && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          disabled={isDownloading}
          onClick={handleDownload}
          title="Download"
        >
          <Download className="h-4 w-4" />
        </Button>
      )}
      {!disabled && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onRemove}
          title="Remove"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};

export const FileField: React.FC<FileFieldProps> = ({
  id,
  field,
  value,
  onChange,
  disabled,
  error,
  caseRecordId,
}) => {
  const organizationId = useStore(state => state.selectedOrganizationId);
  const programId = useStore(state => state.selectedProgramId);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  // Name/size of files uploaded this session, keyed by asset id, so they display immediately
  // without waiting for the asset-metadata fetch (and never leak onto a different value).
  const [uploadedMeta, setUploadedMeta] = useState<Record<string, { name: string; size: number }>>(
    {},
  );

  // Schema-declared constraints (fileMetadata), falling back to the historical
  // defaults. typeCaps maps a lowercased extension → per-file byte cap.
  const metadata = field.fileMetadata;
  const declared = !!metadata;
  const typeCaps = new Map<string, number>(
    metadata?.supportedFiles?.length
      ? metadata.supportedFiles.map(s => [
          s.fileType.toLowerCase(),
          s.maxSizeBytes || DEFAULT_MAX_FILE_SIZE,
        ])
      : DEFAULT_ACCEPTED_FORMATS.map(ext => [ext, DEFAULT_MAX_FILE_SIZE]),
  );
  const acceptedFormats = [...typeCaps.keys()];
  // Declared fields always carry a list value; max_count bounds it (0 = unbounded).
  const maxCount = declared ? metadata.maxCount || 0 : 1;
  const allowMultiple = maxCount !== 1;

  const entries = toEntries(value);
  const pendingFiles = entries.filter((e): e is File => e instanceof File);

  const emit = (next: FileEntry[]): void => {
    if (declared) {
      // Declared fields always store a list of asset ids / pending Files.
      onChange(next.length > 0 ? next : undefined);
    } else {
      onChange(next[0]);
    }
  };

  const validate = (file: File): boolean => {
    const dotIdx = file.name.lastIndexOf('.');
    const ext = dotIdx >= 0 ? file.name.slice(dotIdx).toLowerCase() : '';
    const cap = typeCaps.get(ext);
    if (cap === undefined) {
      toast.error(`File format not allowed. Allowed: ${acceptedFormats.join(', ')}`);
      return false;
    }
    if (file.size > cap) {
      toast.error(`${ext} files are limited to ${formatSize(cap) || 'the size cap'}`);
      return false;
    }
    return true;
  };

  const uploadFile = async (file: File): Promise<string | undefined> => {
    const resp = await assetService.createAsset({
      organization_id: organizationId!,
      program_id: programId!,
      case_record_id: caseRecordId,
      // The generated client appends a Blob/File directly; the typed field is Uint8Array.
      data: file as unknown as Uint8Array,
      filename: file.name,
      content_type: file.type || 'application/octet-stream',
      asset_type: USER_ATTACHMENT,
      // Lets the backend apply this field's schema-declared constraints.
      field_id: field.id,
    });
    if (!resp.asset_id) return undefined;
    setUploadedMeta(prev => ({
      ...prev,
      [resp.asset_id!]: { name: file.name, size: file.size },
    }));
    return resp.asset_id;
  };

  const handleSelect = async (picked: File[]): Promise<void> => {
    const valid = picked.filter(validate);
    if (valid.length === 0) return;

    const room = maxCount > 0 ? maxCount - entries.length : Infinity;
    if (valid.length > room) {
      toast.error(`At most ${maxCount} files are allowed`);
      return;
    }

    // Manual Case Creation: no case exists yet — defer upload, keep the Files in form state.
    if (!caseRecordId) {
      emit([...entries, ...valid]);
      return;
    }

    // Existing case: upload immediately and store the returned asset IDs.
    setIsUploading(true);
    try {
      const uploaded: string[] = [];
      for (const file of valid) {
        const assetId = await uploadFile(file);
        if (!assetId) {
          toast.error('Upload did not return an asset id. Please try again.');
          break;
        }
        uploaded.push(assetId);
      }
      if (uploaded.length > 0) {
        emit([...entries, ...uploaded]);
      }
    } catch (e) {
      toast.error(uploadErrorMessage(e));
    } finally {
      setIsUploading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const picked = Array.from(e.target.files ?? []);
    // Reset so selecting the same file again still fires onChange.
    e.target.value = '';
    if (picked.length > 0) void handleSelect(picked);
  };

  // Flush files deferred during Manual Create (picked before any case id existed) as soon as a
  // case becomes available — e.g. when the user links a call. Keyed on caseRecordId so it runs
  // once when the case appears; after upload the values become asset ids, so it won't re-fire.
  useEffect(() => {
    const flush = async (): Promise<void> => {
      setIsUploading(true);
      try {
        const next: FileEntry[] = [];
        for (const entry of entries) {
          if (entry instanceof File) {
            const assetId = await uploadFile(entry);
            if (assetId) next.push(assetId);
          } else {
            next.push(entry);
          }
        }
        emit(next);
      } catch (e) {
        toast.error(uploadErrorMessage(e));
      } finally {
        setIsUploading(false);
      }
    };
    if (caseRecordId && pendingFiles.length > 0 && !isUploading) {
      void flush();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseRecordId]);

  const isDisabled = disabled || isUploading;
  const canAddMore = !isDisabled && (maxCount === 0 || entries.length < maxCount);

  return (
    // tabIndex -1 keeps the wrapper out of the tab order while still allowing a
    // programmatic focus() to land on it.
    <div id={id} tabIndex={-1} className="space-y-2 focus:outline-none">
      <input
        ref={inputRef}
        type="file"
        accept={acceptedFormats.join(',')}
        multiple={allowMultiple}
        className="hidden"
        onChange={handleInputChange}
        disabled={isDisabled}
      />

      {entries.map((entry, idx) => (
        <FileEntryRow
          key={typeof entry === 'string' ? entry : `${entry.name}-${idx}`}
          entry={entry}
          fieldLabel={field.label}
          caseRecordId={caseRecordId}
          localMeta={typeof entry === 'string' ? uploadedMeta[entry] : undefined}
          disabled={isDisabled}
          error={!!error}
          onRemove={() => emit(entries.filter((_, i) => i !== idx))}
        />
      ))}

      {isUploading && (
        <div className="flex items-center gap-2 rounded-md border p-2">
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Uploading…</span>
        </div>
      )}

      {entries.length === 0 && !isUploading ? (
        <Button
          type="button"
          variant="outline"
          disabled={isDisabled}
          onClick={() => inputRef.current?.click()}
          className={`w-full justify-center gap-2 ${error ? 'border-destructive' : ''}`}
        >
          <Upload className="h-4 w-4" />
          {allowMultiple ? 'Upload files' : 'Upload file'}
        </Button>
      ) : (
        allowMultiple &&
        canAddMore && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => inputRef.current?.click()}
            className="gap-2"
          >
            <Upload className="h-3.5 w-3.5" />
            Add more{maxCount > 0 ? ` (${entries.length}/${maxCount})` : ''}
          </Button>
        )
      )}
    </div>
  );
};
